import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { exhaustMap, filter, from, interval, tap } from 'rxjs';

import { injectDestroySink } from '../../../core/inject-destroy-sink';
import { UnrecoverableErrorComponent } from './unrecoverable-error/unrecoverable-error.component';
import { UpdateAvailableComponent } from './update-available/update-available.component';
import { VERSION_WATCHER_OPTIONS } from './version-watcher-options';

@Injectable()
export class PwaAppVersionService {
  private options = inject(VERSION_WATCHER_OPTIONS);
  private swUpdate = inject(SwUpdate);
  private snackBar = inject(MatSnackBar);
  private sink = injectDestroySink();

  constructor() {
    if (this.swUpdate.isEnabled) {
      this.watchForNewVersionReady();
      this.checkForUpdatesEverySixHours();
      this.watchForUnrecoverableErrors();
    }
  }

  private watchForNewVersionReady() {
    const $ = this.swUpdate.versionUpdates.pipe(
      filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
      tap(() => this.snackBar.openFromComponent(UpdateAvailableComponent))
    );

    this.sink.subscribeTo($);
  }

  private watchForUnrecoverableErrors() {
    const $ = this.swUpdate.unrecoverable.pipe(
      tap((event) => {
        this.snackBar.openFromComponent(UnrecoverableErrorComponent, {
          data: event.reason,
        });
      })
    );

    this.sink.subscribeTo($);
  }

  private checkForUpdatesEverySixHours() {
    const onInterval$ = interval(this.options.checkInterval);

    this.sink.subscribeTo(
      onInterval$.pipe(exhaustMap(() => from(this.checkForUpdates())))
    );
  }

  async checkForUpdates() {
    return await this.swUpdate.checkForUpdate();
  }
}
